<template>
  <v-dialog :value="value" @input="$emit('input', $event)" :fullscreen="$vuetify.breakpoint.smAndDown" max-width="800" scrollable>
    <v-card v-if="order" dark>
      <v-card-title>
        <v-icon left>mdi-package-variant-closed</v-icon>
        Bestellung #{{ order._id.slice(-6).toUpperCase() }}
        <v-spacer></v-spacer>
        <v-chip small :color="statusColor(order.status)" dark class="mr-2">{{ statusLabel(order.status) }}</v-chip>
        <v-btn icon @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text style="max-height: 70vh">
        <!-- Status-Tracker -->
        <div class="status-tracker mt-4">
          <div
            v-for="(step, idx) in statusSteps"
            :key="step.value"
            class="step"
            :class="{ active: isStepActive(order.status, step.value), current: order.status === step.value }">
            <div class="step-icon">
              <v-icon size="20" :color="isStepActive(order.status, step.value) ? 'white' : 'grey darken-1'">{{ step.icon }}</v-icon>
            </div>
            <div class="step-label">{{ step.label }}</div>
            <div
              v-if="idx < statusSteps.length - 1"
              class="step-line"
              :class="{ active: isStepActive(order.status, statusSteps[idx + 1].value) }"></div>
          </div>
        </div>

        <!-- Status-Management (Manage) -->
        <v-card v-if="canManage" dark color="rgba(255,255,255,0.04)" flat class="mt-6 pa-3">
          <div class="caption grey--text mb-2">Status verwalten</div>
          <v-btn-toggle :value="order.status" mandatory dense @change="changeStatus" color="primary">
            <v-btn small value="received">Erhalten</v-btn>
            <v-btn small value="confirmed">Bestätigt</v-btn>
            <v-btn small value="shipping">Unterwegs</v-btn>
            <v-btn small value="delivered">Geliefert</v-btn>
          </v-btn-toggle>
        </v-card>

        <!-- Allgemein -->
        <h3 class="section-title mt-6">
          <v-icon left small>mdi-information-outline</v-icon>
          Allgemein
        </h3>
        <div class="info-grid">
          <div class="info-row">
            <span class="info-label">Datum</span>
            <span class="info-value">{{ formatDateTime(order.createdAt) }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Studio</span>
            <span class="info-value">{{ order.studio ? order.studio.name : "-" }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Erstellt von</span>
            <span class="info-value">{{ order.createdBy ? order.createdBy.vorname + " " + order.createdBy.nachname : "-" }}</span>
          </div>
          <div v-if="order.createdBy && order.createdBy.email" class="info-row">
            <span class="info-label">E-Mail</span>
            <span class="info-value">{{ order.createdBy.email }}</span>
          </div>
        </div>

        <!-- Artikel -->
        <div class="d-flex align-center mt-6 mb-3">
          <h3 class="section-title mb-0">
            <v-icon left small>mdi-cart</v-icon>
            Artikel ({{ totalItems }})
          </h3>
          <v-spacer></v-spacer>
          <v-btn v-if="canEdit && !editMode" small text color="primary" @click="enterEditMode">
            <v-icon left small>mdi-pencil</v-icon>
            Bearbeiten
          </v-btn>
          <template v-if="editMode">
            <v-btn small text @click="cancelEdit" :disabled="savingEdit">Abbrechen</v-btn>
            <v-btn small color="primary" depressed :loading="savingEdit" :disabled="editItems.length === 0" @click="saveEdit">
              <v-icon left small>mdi-content-save</v-icon>
              Speichern
            </v-btn>
          </template>
        </div>

        <!-- View-Modus -->
        <div v-if="!editMode" class="items-list">
          <div v-for="(item, idx) in order.items" :key="idx" class="item-row">
            <div class="item-name">
              <v-icon v-if="item.productType === 'digital'" small color="primary" class="mr-1">mdi-download</v-icon>
              {{ item.productTitle }}
            </div>
            <div class="item-qty d-flex align-center" style="gap: 6px">
              <v-btn v-if="item.productType === 'digital' && item.file" icon small color="primary" @click="downloadItem(idx, item)">
                <v-icon small>mdi-download</v-icon>
              </v-btn>
              <v-chip x-small color="primary" dark>{{ item.quantity }} Stk</v-chip>
            </div>
          </div>
        </div>

        <!-- Edit-Modus -->
        <div v-else>
          <div class="items-list edit">
            <div v-for="(item, idx) in editItems" :key="idx" class="item-row-edit">
              <div class="item-name flex-grow-1">
                <v-icon v-if="item.productType === 'digital'" small color="primary" class="mr-1">mdi-download</v-icon>
                {{ item.productTitle }}
              </div>
              <div v-if="item.productType !== 'digital'" class="qty-stepper-edit">
                <v-btn icon x-small @click="changeEditQty(idx, -1)" :disabled="item.quantity <= (item.minQuantity || 1)">
                  <v-icon small>mdi-minus</v-icon>
                </v-btn>
                <input
                  v-model.number="item.quantity"
                  type="number"
                  class="qty-input"
                  :min="item.minQuantity || 1"
                  :max="item.maxQuantity || 100"
                  @blur="clampEditQty(idx)" />
                <v-btn icon x-small @click="changeEditQty(idx, 1)" :disabled="item.quantity >= (item.maxQuantity || 100)">
                  <v-icon small>mdi-plus</v-icon>
                </v-btn>
              </div>
              <span v-else class="caption grey--text mr-2">1 Stk</span>
              <v-btn icon small color="error" @click="removeEditItem(idx)">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Produkt hinzufügen -->
          <div class="add-product-section mt-4">
            <v-autocomplete
              v-model="newProductId"
              :items="addableProducts"
              item-text="title"
              item-value="_id"
              label="Produkt hinzufügen"
              outlined
              dense
              dark
              hide-details
              clearable
              @change="addProductToEdit" />
          </div>
        </div>

        <!-- Notiz -->
        <h3 class="section-title mt-6">
          <v-icon left small>mdi-note-text</v-icon>
          Notiz
        </h3>
        <div v-if="!editMode">
          <div v-if="order.note" class="note-block">{{ order.note }}</div>
          <div v-else class="caption grey--text">Keine Notiz vorhanden.</div>
        </div>
        <v-textarea v-else v-model="editNote" placeholder="Notiz zur Bestellung..." outlined dense dark rows="3" hide-details auto-grow />

        <!-- Kommentare -->
        <h3 class="section-title mt-6">
          <v-icon left small>mdi-comment-multiple</v-icon>
          Kommentare ({{ order.comments ? order.comments.length : 0 }})
        </h3>
        <div v-if="order.comments && order.comments.length > 0">
          <v-card v-for="(c, idx) in order.comments" :key="idx" class="mb-2 pa-3" color="rgba(255,255,255,0.05)" flat>
            <div class="d-flex justify-space-between align-center mb-1">
              <strong>{{ c.user ? c.user.vorname + " " + c.user.nachname : "Unbekannt" }}</strong>
              <span class="caption grey--text">{{ formatDateTime(c.createdAt) }}</span>
            </div>
            <div style="white-space: pre-wrap">{{ c.comment }}</div>
          </v-card>
        </div>
        <p v-else class="grey--text caption">Noch keine Kommentare.</p>

        <v-textarea v-model="newComment" label="Kommentar hinzufügen" outlined dense dark rows="2" class="mt-3" hide-details />
        <div class="text-right mt-2">
          <v-btn small color="primary" :disabled="!newComment.trim()" :loading="savingComment" @click="addComment">
            <v-icon left small>mdi-send</v-icon>Senden
          </v-btn>
        </div>

        <!-- History (nur Manage) -->
        <div v-if="canManage && order.editHistory && order.editHistory.length > 0">
          <h3 class="section-title mt-6">
            <v-icon left small>mdi-history</v-icon>
            Historie ({{ order.editHistory.length }})
          </h3>
          <v-expansion-panels dark flat>
            <v-expansion-panel v-for="(h, idx) in [...order.editHistory].reverse()" :key="idx" style="background: rgba(255, 255, 255, 0.03)">
              <v-expansion-panel-header>
                <div>
                  <strong>{{ h.user ? h.user.vorname + " " + h.user.nachname : "Unbekannt" }}</strong>
                  <span class="caption grey--text ml-2">{{ formatDateTime(h.editedAt) }}</span>
                  <v-chip x-small class="ml-2" color="blue-grey" dark>{{ actionLabel(h.action) }}</v-chip>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div v-for="(c, cidx) in h.changes" :key="cidx" class="mb-2" style="font-size: 13px">
                  <strong>{{ c.field }}:</strong>
                  <span style="color: #c0392b">{{ formatHistoryValue(c.oldValue) }}</span>
                  →
                  <span style="color: #27ae60">{{ formatHistoryValue(c.newValue) }}</span>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions>
        <v-btn v-if="canManage" text color="error" @click="deleteOrder"> <v-icon left>mdi-delete</v-icon>Löschen </v-btn>
        <v-spacer></v-spacer>
        <v-btn text @click="$emit('input', false)">Schliessen</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/api"

export default {
  name: "OrderDetailDialog",
  props: {
    value: { type: Boolean, default: false },
    order: { type: Object, default: null },
    canManage: { type: Boolean, default: false },
    availableProducts: { type: Array, default: () => [] },
  },
  data() {
    return {
      newComment: "",
      savingComment: false,
      editMode: false,
      savingEdit: false,
      editItems: [],
      editNote: "",
      newProductId: null,
      statusSteps: [
        { value: "received", label: "Erhalten", icon: "mdi-inbox-arrow-down" },
        { value: "confirmed", label: "Bestätigt", icon: "mdi-check-circle" },
        { value: "shipping", label: "Unterwegs", icon: "mdi-truck-fast" },
        { value: "delivered", label: "Geliefert", icon: "mdi-package-variant" },
      ],
    }
  },
  computed: {
    totalItems() {
      if (!this.order) return 0
      return (this.order.items || []).reduce((sum, i) => sum + (i.quantity || 0), 0)
    },
    canEdit() {
      // Server liefert canEdit-Flag mit; fallback auf clientseitige Logik
      if (!this.order) return false
      if (typeof this.order.canEdit === "boolean") return this.order.canEdit
      if (this.canManage) return true
      return this.order.status === "received"
    },
    addableProducts() {
      const existingIds = new Set((this.editItems || []).map((i) => String(i.product)))
      return this.availableProducts.filter((p) => !existingIds.has(String(p._id)))
    },
  },
  watch: {
    value(open) {
      if (!open) this.exitEditMode()
    },
    order() {
      // Wenn sich die Order ändert, Edit-Modus zurücksetzen
      if (this.editMode) this.exitEditMode()
    },
  },
  methods: {
    formatDateTime(d) {
      return d ? this.$moment(d).format("DD.MM.YYYY HH:mm") : "-"
    },
    statusColor(s) {
      if (s === "delivered") return "green"
      if (s === "shipping") return "blue"
      if (s === "confirmed") return "amber darken-2"
      return "grey darken-1"
    },
    statusLabel(s) {
      const step = this.statusSteps.find((x) => x.value === s)
      return step ? step.label : "Unbekannt"
    },
    actionLabel(a) {
      if (a === "status_change") return "Status"
      if (a === "deleted") return "Gelöscht"
      if (a === "edit") return "Bearbeitet"
      return a || "Bearbeitet"
    },
    isStepActive(currentStatus, stepValue) {
      const order = ["received", "confirmed", "shipping", "delivered"]
      return order.indexOf(currentStatus) >= order.indexOf(stepValue)
    },
    formatHistoryValue(val) {
      if (val === null || val === undefined || val === "") return "-"
      if (val === "received") return "Erhalten"
      if (val === "confirmed") return "Bestätigt"
      if (val === "shipping") return "Unterwegs"
      if (val === "delivered") return "Geliefert"
      return String(val)
    },
    async changeStatus(status) {
      if (!this.order || this.order.status === status) return
      try {
        await api.marketingShop.updateOrderStatus(this.order._id, status)
        this.$emit("reload")
        this.$emit("order-updated")
      } catch (e) {
        console.error("Statuswechsel fehlgeschlagen:", e)
        alert("Fehler beim Statuswechsel")
      }
    },
    async addComment() {
      if (!this.newComment.trim() || !this.order) return
      this.savingComment = true
      try {
        await api.marketingShop.addOrderComment(this.order._id, this.newComment)
        this.newComment = ""
        this.$emit("reload")
      } catch (e) {
        console.error("Kommentar fehlgeschlagen:", e)
      } finally {
        this.savingComment = false
      }
    },
    async deleteOrder() {
      if (!this.order || !confirm("Bestellung wirklich löschen?")) return
      try {
        await api.marketingShop.deleteOrder(this.order._id)
        this.$emit("input", false)
        this.$emit("order-updated")
      } catch (e) {
        console.error("Löschen fehlgeschlagen:", e)
      }
    },
    async downloadItem(idx, item) {
      try {
        const filename = item.file?.originalName || `${item.productTitle}.pdf`
        await api.marketingShop.downloadOrderItemFile(this.order._id, idx, filename)
      } catch (e) {
        console.error("Download fehlgeschlagen:", e)
        alert("Download fehlgeschlagen")
      }
    },

    // ===== Edit-Modus =====
    enterEditMode() {
      // Snapshot der aktuellen Items + min/max aus availableProducts hinzufügen
      this.editItems = (this.order.items || []).map((i) => {
        const prod = this.availableProducts.find((p) => String(p._id) === String(i.product?._id || i.product))
        return {
          product: i.product?._id || i.product,
          productTitle: i.productTitle,
          productType: i.productType || "physical",
          quantity: i.quantity,
          minQuantity: prod?.minQuantity || 1,
          maxQuantity: prod?.maxQuantity || 100,
        }
      })
      this.editNote = this.order.note || ""
      this.newProductId = null
      this.editMode = true
    },
    exitEditMode() {
      this.editMode = false
      this.editItems = []
      this.editNote = ""
      this.newProductId = null
    },
    cancelEdit() {
      this.exitEditMode()
    },
    changeEditQty(idx, delta) {
      const item = this.editItems[idx]
      if (!item || item.productType === "digital") return
      const min = item.minQuantity || 1
      const max = item.maxQuantity || 100
      item.quantity = Math.max(min, Math.min(max, (item.quantity || min) + delta))
      this.$set(this.editItems, idx, { ...item })
    },
    clampEditQty(idx) {
      const item = this.editItems[idx]
      if (!item || item.productType === "digital") return
      const min = item.minQuantity || 1
      const max = item.maxQuantity || 100
      let v = parseInt(item.quantity) || min
      v = Math.max(min, Math.min(max, v))
      item.quantity = v
      this.$set(this.editItems, idx, { ...item })
    },
    removeEditItem(idx) {
      this.editItems.splice(idx, 1)
    },
    addProductToEdit(productId) {
      if (!productId) return
      const prod = this.availableProducts.find((p) => String(p._id) === String(productId))
      if (!prod) return
      this.editItems.push({
        product: prod._id,
        productTitle: prod.title,
        productType: prod.productType || "physical",
        quantity: prod.productType === "digital" ? 1 : prod.minQuantity || 1,
        minQuantity: prod.minQuantity || 1,
        maxQuantity: prod.maxQuantity || 100,
      })
      this.$nextTick(() => {
        this.newProductId = null
      })
    },
    async saveEdit() {
      if (this.editItems.length === 0) {
        alert("Die Bestellung muss mindestens einen Artikel enthalten.")
        return
      }
      this.savingEdit = true
      try {
        const payload = {
          note: this.editNote || "",
          items: this.editItems.map((i) => ({ product: i.product, quantity: i.quantity })),
        }
        await api.marketingShop.updateOrder(this.order._id, payload)
        this.exitEditMode()
        this.$emit("reload")
        this.$emit("order-updated")
      } catch (e) {
        console.error("Speichern fehlgeschlagen:", e)
        const msg = e.response?.data?.error || "Fehler beim Speichern"
        alert(msg)
      } finally {
        this.savingEdit = false
      }
    },
  },
}
</script>

<style scoped>
.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  letter-spacing: 0.5px;
}

.status-tracker {
  display: flex;
  padding: 8px 4px;
}

.step {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.step-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid rgba(255, 255, 255, 0.1);
  z-index: 2;
  transition: all 0.3s ease;
}

.step.active .step-icon {
  background: #009640;
  border-color: #009640;
}

.step.current .step-icon {
  background: #009640;
  border-color: #4ade80;
  box-shadow: 0 0 0 4px rgba(74, 222, 128, 0.3);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%,
  100% {
    box-shadow: 0 0 0 4px rgba(74, 222, 128, 0.3);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(74, 222, 128, 0.15);
  }
}

.step-label {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 8px;
  font-weight: 500;
}

.step.active .step-label {
  color: #fff;
}

.step-line {
  position: absolute;
  top: 20px;
  left: 50%;
  width: 100%;
  height: 2px;
  background: rgba(255, 255, 255, 0.1);
  z-index: 1;
}

.step-line.active {
  background: #009640;
}

.info-grid {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  padding: 4px 0;
}

.info-row {
  display: flex;
  padding: 8px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  font-size: 13.5px;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  flex: 0 0 140px;
  color: rgba(255, 255, 255, 0.55);
}

.info-value {
  flex: 1;
  color: #fff;
}

.items-list {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  overflow: hidden;
}

.items-list.edit {
  background: rgba(0, 150, 64, 0.05);
  border: 1px solid rgba(0, 150, 64, 0.2);
}

.item-row {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.item-row:last-child {
  border-bottom: none;
}

.item-row-edit {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  gap: 8px;
}

.item-row-edit:last-child {
  border-bottom: none;
}

.item-name {
  flex-grow: 1;
  font-size: 13.5px;
  display: flex;
  align-items: center;
}

.qty-stepper-edit {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 999px;
  padding: 2px;
}

.qty-input {
  width: 36px;
  text-align: center;
  background: transparent;
  border: none;
  color: #fff;
  font-weight: 600;
  font-size: 13px;
  outline: none;
  -moz-appearance: textfield;
}

.qty-input::-webkit-outer-spin-button,
.qty-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.add-product-section {
  background: rgba(255, 255, 255, 0.03);
  padding: 10px;
  border-radius: 8px;
}

.note-block {
  background: rgba(255, 255, 255, 0.03);
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 13.5px;
  white-space: pre-wrap;
  border-left: 3px solid #009640;
}
</style>
